import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchClinic.css';
import { Clinic } from '../types/clinic';
import React from 'react';

// Define the ref type
export interface SearchClinicRef {
    clearSearch: () => void;
    getClinics: () => Clinic[];
    onClinicsLoaded: (clinics: Clinic[]) => void;
    setSearchInput: (input: string) => void;
}

interface SearchClinicProps {
    onSelectClinic?: (clinic: Clinic) => void;
    autoFocus?: boolean;
}

const SearchClinic = forwardRef<SearchClinicRef, SearchClinicProps>(({ onSelectClinic, autoFocus = false }, ref) => {
    const [searchInput, setSearchInput] = useState<string>('');
    const [suggestions, setSuggestions] = useState<Clinic[]>([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const [selectedClinicLogo, setSelectedClinicLogo] = useState<Buffer | null>(null);
    // Ref to store the callback function for when clinics are loaded
    const clinicsLoadedCallbackRef = useRef<(clinics: Clinic[]) => void | null>(null);

    // Expose methods to parent components
    useImperativeHandle(ref, () => ({
        clearSearch: () => {
            setSearchInput('');
            setSuggestions([]);
            setActiveSuggestionIndex(-1);
        },
        getClinics: () => clinics,
        set onClinicsLoaded(callback: (clinics: Clinic[]) => void) {
            clinicsLoadedCallbackRef.current = callback;
            // If clinics are already loaded, call the callback immediately
            if (!isLoading && clinics.length > 0) {
                callback(clinics);
            }
        },
        setSearchInput: (input: string) => {
            setSearchInput(input);
        }
    }));

    useEffect(() => {
        // Only focus on initial render if autoFocus is true
        if (autoFocus && searchInputRef.current) {
            // Use timeout to ensure the DOM is fully ready
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 50);
        }
    }, [autoFocus]);

    // The logo is intentionally not restored from storage. Only the clinicCode
    // is persisted (see utils/clinicStorage.ts); every other clinic attribute,
    // including the logo, comes from the freshly fetched clinic list so a change
    // in Foundation is reflected immediately. The header logo for an already
    // selected clinic is rendered by App.tsx from the clinicSelected event.

    useEffect(() => {
        const fetchClinics = async () => {
            try {
                setIsLoading(true);
                
                // Check if we have cached clinics data in sessionStorage
                // const cachedClinics = sessionStorage.getItem('cachedClinics');
                // if (cachedClinics) {
                //     const parsedClinics = JSON.parse(cachedClinics);
                //     setClinics(parsedClinics);
                    
                //     // Call the callback if registered
                //     if (clinicsLoadedCallbackRef.current) {
                //         clinicsLoadedCallbackRef.current(parsedClinics);
                //     }
                    
                //     setIsLoading(false);
                //     return; // Skip the API call
                // }
                
                // Get the API key from environment variables
                const apiKey = import.meta.env.VITE_REACT_APP_REFERRAL_API_KEY || '';
                
                const response = await fetch('/api/clinics', {
                    headers: {
                        'Accept': 'application/json',
                        'x-api-key': apiKey
                    },
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch clinics: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Define an interface for the raw clinic data from API
                interface RawClinicData {
                    name?: string;
                    address?: string;
                    phone?: string;
                    fax?: string;
                    clinicianNames?: string[] | string;
                    clinicians?: string[] | string;
                    speciality?: string;
                    clinicLogo?: string;
                    logo?: string; // Alternative name for logo
                    clinicCode?: string;
                    [key: string]: unknown; // Allow for other properties with unknown type
                }
                
                // Normalize the data to ensure all required properties exist
                const normalizedData = data?.map((clinic: RawClinicData) => {
                    // Handle clinicians vs clinicianNames property name difference
                    let providers: string[] = [];
                    
                    if (Array.isArray(clinic.clinicianNames)) {
                        providers = clinic.clinicianNames;
                    } else if (Array.isArray(clinic.clinicians)) {
                        // If API returns 'clinicians' instead of 'clinicianNames'
                        providers = clinic.clinicians;
                    } else if (typeof clinic.clinicians === 'string') {
                        // If API returns clinicians as a comma-separated string
                        providers = clinic.clinicians.split(',').map((name: string) => name.trim());
                    } else if (typeof clinic.clinicianNames === 'string') {
                        // If API returns clinicianNames as a comma-separated string
                        providers = clinic.clinicianNames.split(',').map((name: string) => name.trim());
                    }

                    // Handle both possible logo property names
                    const logoUrl = clinic.clinicLogo || clinic.logo || '';
                    
                    return {
                        name: clinic.name || '',
                        address: clinic.address || '',
                        phone: clinic.phone || '',
                        fax: clinic.fax || '',
                        clinicianNames: providers,
                        speciality: clinic.speciality || '',
                        clinicLogo: logoUrl,
                        clinicCode: clinic.clinicCode || ''
                    };
                });
                
                // Cache the normalized clinics data in localStorage
                // sessionStorage.setItem('cachedClinics', JSON.stringify(normalizedData));
                
                setClinics(normalizedData);
                
                // If there's a callback waiting for clinics to load, call it now
                if (clinicsLoadedCallbackRef.current) {
                    clinicsLoadedCallbackRef.current(normalizedData);
                }
                
                setError(null);
            } catch (err) {
                console.error('Error fetching clinics:', err);
                setError('Failed to load clinics. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchClinics();
    }, []);

    useEffect(() => {
        // Only perform search when user enters at least 3 characters
        if (searchInput.length >= 3) {
            const filteredSuggestions = clinics.filter(clinic =>
                clinic.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
                clinic.address?.toLowerCase().includes(searchInput.toLowerCase()) ||
                clinic.phone?.includes(searchInput) ||
                clinic.fax?.includes(searchInput) ||
                (Array.isArray(clinic.clinicianNames) && clinic.clinicianNames.some(name => 
                    name && name.toLowerCase().includes(searchInput.toLowerCase())
                ))
            );
            setSuggestions(filteredSuggestions.slice(0, 5)); // Show up to 5 suggestions only
        } else {
            // Clear suggestions if input is less than 3 characters
            setSuggestions([]);
        }
        setActiveSuggestionIndex(-1);
    }, [searchInput, clinics]);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleSuggestionClick = (clinic: Clinic) => {
        setSearchInput(clinic.name);
        setSuggestions([]);
        handleSelectClinic(clinic);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Only handle arrow keys and enter if there are suggestions
        if (suggestions.length === 0) {
            return;
        }
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveSuggestionIndex((prevIndex) =>
                prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveSuggestionIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : prevIndex
            );
        } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
            e.preventDefault();
            handleSuggestionClick(suggestions[activeSuggestionIndex]);
        }
    };

    const handleSelectClinic = (clinic: Clinic) => {
        // The selection is deliberately not persisted - see utils/clinicStorage.ts.
        // It lives in React state for this page load only.

        // Update to use clinicLogo property
        if (clinic.clinicLogo) {
            setSelectedClinicLogo(clinic.clinicLogo.data);
        } else {
            console.log('No clinic logo found in selected clinic:', clinic);
        }

        // The clinicName cookie that used to be written here has been removed:
        // it was never read back, and it was a third copy of a name that goes
        // stale the moment the clinic is renamed in Foundation.

        if (onSelectClinic) {
            onSelectClinic(clinic);
        } else {
            navigate('/create-referral');
        }
    };

    // Function to highlight matching text in suggestions
    const highlightMatch = (text: string) => {
        if (!searchInput || !text) return text;
        
        const parts = text.split(new RegExp(`(${searchInput})`, 'gi'));
        return parts.map((part, index) => 
            part.toLowerCase() === searchInput.toLowerCase() 
                ? <span key={index} className="highlight">{part}</span> 
                : part
        );
    };

    return (
        <div className="search-clinic-component bg-white text-gray-800 relative">
            {/* Add logo display with higher z-index and more visible styling */}
            {selectedClinicLogo && (
                <div 
                    className="fixed top-4 right-4 z-50"
                    style={{
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        background: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    <img 
                        src={`data:image/jpeg;base64,${btoa(String.fromCharCode(...new Uint8Array(selectedClinicLogo)))}`} 
                        alt="Selected clinic logo" 
                        className="h-16 w-auto object-contain"
                        style={{ maxWidth: '120px' }}
                        onError={(e) => {
                            console.error('Error loading clinic logo');
                            // Replace with a placeholder instead of hiding
                            const target = e.target as HTMLImageElement;
                            // Use a simple colored placeholder with text
                            target.style.width = '100px';
                            target.style.height = '50px';
                            target.style.display = 'flex';
                            target.style.alignItems = 'center';
                            target.style.justifyContent = 'center';
                            target.style.backgroundColor = '#f3f4f6';
                            target.style.borderRadius = '4px';
                            target.style.color = '#6b7280';
                            target.style.fontSize = '10px';
                            // Remove src to prevent further loading attempts
                            target.removeAttribute('src');
                            target.setAttribute('alt', 'Clinic logo unavailable');
                        }}
                    />
                </div>
            )}
            
            {isLoading ? (
                <div className="loading-message text-gray-700">
                    <p>Loading clinics data...</p>
                </div>
            ) : error ? (
                <div className="error-message text-red-600">
                    <p>{error}</p>
                </div>
            ) : (
                <>
                    <div className="search-inputs">
                        <div className="search-input-wrapper">
                            <i className="search-icon text-gray-500">🔍</i>
                            <input
                                ref={searchInputRef}
                                className="search-input bg-white text-gray-800 text-lg"
                                type="text"
                                placeholder="Search by name, address, or phone number"
                                value={searchInput}
                                onChange={handleSearchInputChange}
                                onKeyDown={handleKeyDown}
                                autoComplete="off"
                            />
                            {searchInput && (
                                <button 
                                    className="clear-search text-gray-500 hover:text-gray-700" 
                                    onClick={() => setSearchInput('')}
                                    aria-label="Clear search"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        
                        {/* Hint for minimum search length */}
                        {searchInput.length > 0 && searchInput.length < 3 && (
                            <div className="search-hint text-xs text-gray-500 mt-1 ml-1">
                                Please enter at least 3 characters to search
                            </div>
                        )}
                        
                        <div className="suggestions-container">
                            {suggestions.length > 0 && (
                                <ul className="suggestions-list bg-white" role="listbox" style={{ gap: 0, padding: '2px 0' }}>
                                    {suggestions.map((clinic, index) => (
                                        <li
                                            key={index}
                                            role="option"
                                            aria-selected={index === activeSuggestionIndex}
                                            className={`suggestion-item text-gray-800 ${index === activeSuggestionIndex ? 'active bg-blue-50' : ''}`}
                                            onClick={() => handleSuggestionClick(clinic)}
                                            style={{ 
                                                display: 'block', 
                                                padding: '5px 10px',
                                                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                margin: 0
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                                                <div style={{ width: '45%', paddingRight: '12px' }}>
                                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827', lineHeight: 1.2 }}>{highlightMatch(clinic.name)}</div>
                                                    <div className="text-sm text-gray-600 truncate" style={{ marginTop: '2px' }}>{highlightMatch(clinic.address)}</div>
                                                </div>
                                                
                                                <div style={{ width: '55%', paddingLeft: '12px', borderLeft: '1px solid #e5e7eb' }}>
                                                    <div className="text-sm text-gray-500" style={{ wordBreak: 'break-word', lineHeight: 1.2 }}>
                                                        <span className="text-xs font-semibold text-gray-600">Providers: </span>
                                                        {clinic.clinicianNames.length > 0 ? 
                                                            clinic.clinicianNames.map((provider, i) => (
                                                                <React.Fragment key={i}>
                                                                    {i > 0 && <span>, </span>}
                                                                    {highlightMatch(provider)}
                                                                </React.Fragment>
                                                            )) : 
                                                            "None"
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                    <li
                                        role="option"
                                        className="suggestion-item text-gray-800 bg-gray-50"
                                        onClick={() => onSelectClinic && onSelectClinic({ 
                                            name: "Other", 
                                            address: "", 
                                            phone: "", 
                                            fax: "", 
                                            clinicianNames: [],
                                            speciality: "custom" 
                                        })}
                                        style={{ 
                                            display: 'block', 
                                            padding: '8px 10px',
                                            borderTop: '1px solid #e5e7eb',
                                            margin: 0,
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            color: '#374151'
                                        }}
                                    >
                                        Can't Find My Organization
                                    </li>
                                </ul>
                            )}
                        </div>
                    </div>
                    
                    {suggestions.length === 0 && searchInput.length >= 3 && (
                        <div className="text-gray-700">
                            <div 
                                className="cursor-pointer text-center text-gray-800 py-2 px-3 bg-gray-50 border border-gray-300 rounded-md"
                                onClick={() => onSelectClinic && onSelectClinic({ 
                                    name: "Other", 
                                    address: "", 
                                    phone: "", 
                                    fax: "", 
                                    clinicianNames: [],
                                    speciality: "custom" 
                                })}
                                style={{ 
                                    display: 'block', 
                                    padding: '8px 10px',
                                    borderTop: '1px solid #e5e7eb',
                                    margin: 0,
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    color: '#374151'
                                }}
                            >
                                <span className="text-sm text-gray-700">Can't Find My Organization</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default SearchClinic; 